import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { icons } from '~/assets';
import Button from '~/components/button';
import Container from '~/components/container';
import ContainerFlex from '~/components/containerFlex';
import Dialog from '~/components/dialog';
import Input, { MaskTypes } from '~/components/input';
import Loading from '~/components/loading';
import TableList from '~/components/tableList';
import { useAuth } from '~/hooks/contexts/useAuth';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import { FormField, setFormState } from '~/utils/form';
import { cpfMask, creditCardMask, currencyMask } from '~/utils/masks';
import { IListColCampaing } from '~/views/Resource/Campaing/model';

import * as St from './styles';

export interface DebtNegotiationClientState {
  cpf: string;
  nome: string;
  numeroCartao: string;
  statusCartao: string;
  limiteCreditoUtilizado?: number;
  limiteEmprestimoUtilizado?: number;
  limiteExtraUtilizado?: number;
  limiteTotalUtilizado?: number;
  limiteTotal?: number;
  limiteCompras?: number;
  limiteEmprestimo?: number;
  limite12x?: number;
  scoreInadimplencia?: {
    dataAtualizacao?: string;
    risco?: string;
    score?: number;
  };
  eligibleDetail?: Record<string, unknown>;
}

interface Form {
  cpf: FormField;
}

const INITIAL_FORM: Form = {
  cpf: { name: 'cpf', value: '' },
};

const customerHeader = [
  {
    id: 1,
    value: 'Nome completo',
    maxChar: 28,
    textAlign: 'left',
  },
  {
    id: 2,
    value: 'CPF',
    maxChar: 16,
    textAlign: 'center',
  },
  {
    id: 3,
    value: 'Cartão',
    maxChar: 22,
    textAlign: 'center',
  },
  {
    id: 4,
    value: 'Status do cartão',
    maxChar: 18,
    textAlign: 'center',
  },
  {
    id: 5,
    value: 'Limite total',
    maxChar: 16,
    textAlign: 'center',
  },
  {
    id: 6,
    value: 'Limite de compras',
    maxChar: 18,
    textAlign: 'center',
  },
  {
    id: 7,
    value: 'Limite de empréstimo',
    maxChar: 22,
    textAlign: 'center',
  },
  {
    id: 8,
    value: 'Limite 12x',
    maxChar: 14,
    textAlign: 'center',
  },
  {
    id: 9,
    value: 'Score',
    maxChar: 10,
    textAlign: 'center',
  },
  {
    id: 10,
    value: 'Ação',
    maxChar: 8,
    textAlign: 'center',
  },
];

const formatMoney = (value?: number) =>
  value === undefined || value === null ? '-' : `R$ ${currencyMask(value)}`;

const formatScore = (score?: number) =>
  score === undefined || score === null ? '-' : String(score);

const toClientState = (
  client: DebtNegotiationClientState,
): DebtNegotiationClientState => ({
  ...client,
  limiteTotal: client.limiteTotalUtilizado,
  limiteCompras: client.limiteCreditoUtilizado,
  limiteEmprestimo: client.limiteEmprestimoUtilizado,
  limite12x: client.limiteExtraUtilizado,
});

const normalizeClients = (payload: unknown): DebtNegotiationClientState[] => {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload.map(toClientState);
  }

  if (typeof payload !== 'object') return [];

  const record = payload as Record<string, unknown>;

  if (Array.isArray(record.data)) {
    return record.data.map((item) =>
      toClientState(item as DebtNegotiationClientState),
    );
  }

  if (record.data && typeof record.data === 'object') {
    return [toClientState(record.data as DebtNegotiationClientState)];
  }

  if ('cpf' in record || 'numeroCartao' in record) {
    return [toClientState(record as unknown as DebtNegotiationClientState)];
  }

  return [];
};

const readClientRecord = (payload: unknown) => {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;

  if ('elegivelNegociacao' in record || 'cpf' in record) return record;

  if (record.data && typeof record.data === 'object') {
    return record.data as Record<string, unknown>;
  }

  return null;
};

const ineligibleMessage = (reason: string) => {
  if (reason === 'NAO_ELEGIVEL_AUTORIZADOR') {
    return (
      <>
        <p>Não é possível simular acordo para este cliente.</p>
        <p>O status do cartão não permite negociação.</p>
        <p>
          A geração de acordo está bloqueada para cartões com status
          {' '}
          <strong>Cobrança</strong>
          {' '}
          ou
          <strong>Cancelado Exp./Solic.</strong>
        </p>
      </>
    );
  }

  if (reason === 'NAO_ELEGIVEL_SCORE') {
    return (
      <>
        <p>Não é possível simular acordo para este cliente.</p>
        <p>O score de risco está abaixo do mínimo exigido.</p>
        <p>
          A geração de acordo está bloqueada para clientes com score de risco
          abaixo de 
          {' '}
          <strong>300</strong> 
          {' '}
          <strong>pts</strong>
          .
        </p>
      </>
    );
  }

  if (reason === 'NEGOCIACAO_EM_ANDAMENTO') {
    return (
      <p>
        Não é possível simular acordo para esse cliente, cliente já possui
        {' '}
        <strong>negociação em aberto</strong>
      </p>
    );
  }

  return <p>Não é possível simular acordo para este cliente.</p>;
};

const DebtNegotiationClientSearch = (): JSX.Element => {
  const history = useHistory();
  const { user } = useAuth();
  const checkingEligibility = useRef(false);
  const [form, setForm] = useState<Form>(INITIAL_FORM);
  const [list, setList] = useState<IListColCampaing[][]>([]);
  const [feedback, setFeedback] = useState('');
  const [ineligibleOpen, setIneligibleOpen] = useState(false);
  const [ineligibleReason, setIneligibleReason] = useState('');
  const searchEligibleClient = useApi<DebtNegotiationClientState>(
    Endpoints.debtNegotiation.getEligibleClientSummary,
    ApiMethod.POST,
  );
  const checkEligibleClient = useApi(
    Endpoints.debtNegotiation.getEligibleClient,
    ApiMethod.POST,
  );

  const cpfIsComplete = form.cpf?.unmasked?.length === 11;

  const goToDetail = async (
    _id: unknown,
    item: DebtNegotiationClientState,
  ) => {
    if (checkingEligibility.current) return;

    const cpf = String(item.cpf || '').replace(/\D/g, '');
    const cpfOperador = String(user?.cpf || '').replace(/\D/g, '');

    if (!cpf || !cpfOperador) {
      setFeedback('Não foi possível identificar o cliente ou o operador.');
      return;
    }

    checkingEligibility.current = true;
    setFeedback('');

    try {
      const response = await checkEligibleClient.callApi({
        cpf,
        cpfOperador,
      });

      if (!(response.status >= 200 && response.status < 300)) {
        setFeedback(
          response.mensagem ||
            'Não foi possível consultar a elegibilidade do cliente.',
        );
        return;
      }

      const clientRecord = readClientRecord(response.data);
      const eligible =
        clientRecord && 'elegivelNegociacao' in clientRecord
          ? Boolean(clientRecord.elegivelNegociacao)
          : undefined;

      if (eligible === false) {
        setIneligibleReason(String(clientRecord?.motivoInelegibilidade || ''));
        setIneligibleOpen(true);
        return;
      }

      if (eligible !== true) {
        setFeedback('Não foi possível confirmar a elegibilidade do cliente.');
        return;
      }

      history.push('/negociacao-dividas/cliente', {
        ...item,
        eligibleDetail: clientRecord,
      });
    } catch (error) {
      setFeedback(
        error instanceof Error
          ? error.message
          : 'Não foi possível consultar a elegibilidade do cliente.',
      );
    } finally {
      checkingEligibility.current = false;
    }
  };

  const buildRow = (
    client: DebtNegotiationClientState,
    group: {
      rowSpan?: number;
      hiddenIdentity?: boolean;
      showBottomBorder?: boolean;
    },
  ): IListColCampaing[] => [
    {
      border: true,
      title: 'Nome completo',
      value: client.nome,
      maxChar: 28,
      textAlign: 'left',
      rowSpan: group.hiddenIdentity ? undefined : group.rowSpan,
      hidden: group.hiddenIdentity,
    },
    {
      border: true,
      title: 'CPF',
      value: cpfMask(client.cpf).masked,
      maxChar: 16,
      textAlign: 'center',
      rowSpan: group.hiddenIdentity ? undefined : group.rowSpan,
      hidden: group.hiddenIdentity,
    },
    {
      title: 'Cartão',
      value: creditCardMask(client.numeroCartao).masked,
      maxChar: 22,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Status do cartão',
      value: client.statusCartao,
      maxChar: 18,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Limite total',
      value: formatMoney(client.limiteTotalUtilizado),
      maxChar: 16,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Limite de compras',
      value: formatMoney(client.limiteCreditoUtilizado),
      maxChar: 18,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Limite de empréstimo',
      value: formatMoney(client.limiteEmprestimoUtilizado),
      maxChar: 22,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Limite 12x',
      value: formatMoney(client.limiteExtraUtilizado),
      maxChar: 14,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Score',
      value: formatScore(client.scoreInadimplencia?.score),
      maxChar: 10,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      item: client,
      maxChar: 8,
      textAlign: 'center',
      searchAction: goToDetail,
      showBottomBorder: group.showBottomBorder,
    },
  ];

  const handleSearch = async () => {
    if (!cpfIsComplete || searchEligibleClient.loading) return;

    setFeedback('');
    setList([]);

    try {
      const response = await searchEligibleClient.callApi({
        cpf: form.cpf.unmasked,
      });

      if (!(response.status >= 200 && response.status < 300)) {
        setFeedback(
          response.mensagem || 'Não foi possível consultar o cliente.',
        );
        return;
      }

      const clients = normalizeClients(response.data);

      if (!clients.length) {
        setFeedback('Nenhum cliente elegível encontrado para este CPF.');
        return;
      }

      setList(
        clients.map((client, index) => {
          const isFirstRow = index === 0;
          const isLastRow = index === clients.length - 1;

          return buildRow(client, {
            rowSpan: isFirstRow ? clients.length : undefined,
            hiddenIdentity: !isFirstRow,
            showBottomBorder: !isLastRow,
          });
        }),
      );
    } catch (error) {
      setFeedback(
        error instanceof Error
          ? error.message
          : 'Não foi possível consultar o cliente.',
      );
    }
  };

  const handleClear = () => {
    setForm({
      cpf: { name: 'cpf', value: '', unmasked: '' },
    });
    setList([]);
    setFeedback('');
  };

  return (
    <Container>
      <Dialog
        icon={icons.exclamation}
        open={ineligibleOpen}
        positiveLabel="Entendi"
        positiveAction={() => setIneligibleOpen(false)}
        widthInPixel={460}
      >
        <St.IneligibleMessage>
          {ineligibleMessage(ineligibleReason)}
        </St.IneligibleMessage>
      </Dialog>
      <St.BoxCard>
        <ContainerFlex row space>
          <ContainerFlex padding={16}>
            <Input
              label="CPF"
              name={form.cpf.name}
              value={form.cpf.value}
              onChange={(e) => setFormState(e, form, setForm)}
              placeholder="000.000.000-00"
              maxLength={14}
              mask={MaskTypes.CPF}
            />
          </ContainerFlex>
          <ContainerFlex padding={16} row gap="12" align="flex-end">
            <Button fitContent outlined onClick={handleClear}>
              <span>Limpar pesquisa</span>
            </Button>
            <Button
              fitContent
              onClick={handleSearch}
              disabled={!cpfIsComplete || searchEligibleClient.loading}
              loading={searchEligibleClient.loading}
            >
              <span>Buscar</span>
            </Button>
          </ContainerFlex>
        </ContainerFlex>
        {feedback && <St.Feedback>{feedback}</St.Feedback>}
      </St.BoxCard>

      <Loading
        status={searchEligibleClient.loading || checkEligibleClient.loading}
      />

      {list.length > 0 && (
        <St.BoxCard>
          <TableList customerHeader={customerHeader} list={list} />
        </St.BoxCard>
      )}
    </Container>
  );
};

export default DebtNegotiationClientSearch;
